import { useToast, type ToastTone } from "@/contexts/ToastContext";
import { useT } from "@/contexts/I18nContext";

/**
 * The notification stack.
 *
 * `role="status"` with `aria-live="polite"` so a screen reader announces a
 * result without interrupting whatever is being read. Errors use `alert`,
 * because "that did not save" is worth interrupting for.
 */

/*
  A toast floats, so it takes the shadow rather than a hairline; the tone is a
  4px edge on the left, which survives a colour-blind reader and a screenshot.
*/
const TONES: Record<ToastTone, string> = {
  success: "border-l-positive bg-positive-soft text-ink",
  error: "border-l-danger bg-danger-soft text-ink",
  info: "border-l-line-strong bg-white text-ink",
};

export function ToastStack() {
  const { toasts, dismiss } = useToast();
  const t = useT();

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role={toast.tone === "error" ? "alert" : "status"}
          className={`pointer-events-auto flex items-start gap-3 rounded-card border-l-4 px-4 py-3
                      text-[14px] shadow-pop ${TONES[toast.tone]}`}
        >
          <span className="flex-1">
            {t(toast.key, toast.params)}
            {/* Said again, counted rather than repeated down the screen. */}
            {toast.count > 1 ? (
              <span className="ml-2 text-ink-muted">
                {t("toast.repeat", { count: toast.count })}
              </span>
            ) : null}
          </span>
          <button
            type="button"
            onClick={() => dismiss(toast.id)}
            aria-label={t("common.close")}
            className="-mr-1 -mt-0.5 rounded-control p-1 text-ink-muted hover:bg-black/5 hover:text-ink"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      ))}
    </div>
  );
}
